import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { Box, Button, Chip, CircularProgress, Snackbar, Typography } from '@material-ui/core'
import { Refresh as RefreshIcon } from '@material-ui/icons'
import axios from 'axios'
import ServiceSidebar from './ServiceSidebar'
import ServiceDetail from './ServiceDetail'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const EstimatingCockpit = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const { url } = props

  const [project, setProject] = useState(null)
  const [projectServices, setProjectServices] = useState([])
  const [services, setServices] = useState([])
  const [selectedServiceId, setSelectedServiceId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recalculating, setRecalculating] = useState(false)
  const [recalcMsg, setRecalcMsg] = useState('')

  // silent=true skips the loading spinner (for background refreshes after user actions)
  const fetchData = useCallback(async (silent) => {
    try {
      if (!silent) setLoading(true)
      const [projRes, psRes, svcRes] = await Promise.all([
        axios.get(`${url}/api/bid-estimation/projects/${projectId}`),
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/project-services`),
        axios.get(`${url}/api/bid-estimation/services`)
      ])
      setProject(projRes.data)
      setProjectServices(Array.isArray(psRes.data) ? psRes.data : [])
      setServices(Array.isArray(svcRes.data) ? svcRes.data : [])
    } catch (err) {
      console.error('EstimatingCockpit fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [url, projectId])

  useEffect(() => { fetchData(false) }, [fetchData])

  const refreshSilent = () => fetchData(true)
  const handleServiceAdded = () => refreshSilent()
  const handleServiceRemoved = () => {
    setSelectedServiceId(null)
    refreshSilent()
  }
  const handleServiceUpdated = () => refreshSilent()

  const handleRecalculateAll = async () => {
    try {
      setRecalculating(true)
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/project-services/recalculate-all`)
      setRecalcMsg(`Recalculated ${res.data.recalculated} of ${res.data.total} services`)
      refreshSilent()
    } catch (err) {
      setRecalcMsg('Recalculate failed')
    } finally {
      setRecalculating(false)
    }
  }

  const selectedService = projectServices.find(ps => ps._id === selectedServiceId)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    )
  }

  const conditionChips = (project?.conditionModifiers || []).filter(m => m.enabled)

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Projects', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Estimating' }
        ]}
        title={`Estimating — ${project?.name || ''}`}
        meta={conditionChips.length > 0 ? conditionChips.map(m => `${m.name} ${Math.round(m.factor * 100)}%`).join(' · ') : null}
        actions={
          <Button
            variant="outlined"
            size="small"
            startIcon={recalculating ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleRecalculateAll}
            disabled={recalculating || projectServices.length === 0}
            style={{ textTransform: 'none' }}
          >
            Recalculate All
          </Button>
        }
      />
      <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <ServiceSidebar
        projectServices={projectServices}
        services={services}
        selectedServiceId={selectedServiceId}
        onSelectService={setSelectedServiceId}
        onServiceAdded={handleServiceAdded}
        onServiceRemoved={handleServiceRemoved}
        projectId={projectId}
        url={url}
      />
      <Box style={{ flex: 1, overflow: 'auto', padding: 20, backgroundColor: '#fafafa' }}>
        <Snackbar
          open={!!recalcMsg}
          autoHideDuration={3000}
          onClose={() => setRecalcMsg('')}
          message={recalcMsg}
        />

        {selectedService ? (
          <ServiceDetail
            projectService={selectedService}
            project={project}
            url={url}
            onUpdate={handleServiceUpdated}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Typography color="textSecondary">
              Select a service from the sidebar to start estimating.
            </Typography>
          </Box>
        )}
      </Box>
      </Box>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(EstimatingCockpit)
